import styled from 'styled-components';

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  font-family: 'Inter', sans-serif;
  padding: 24px;
  gap: 24px;
  margin: 0 auto;
`;

export const BreadcrumbRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;

  > div {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  > span,
  > div > span {
    font-family: 'DM Sans', sans-serif;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 25px;
    letter-spacing: 0.2px;
  }

  .link {
    color: var(--Primary-Purple, #5768ff);
  }

  .separator {
    color: var(--escala-de-cinza-40, #a4a4a4);
  }

  .text {
    color: var(--escala-de-cinza-40, #a4a4a4);
  }

  .expiry-tag {
    display: flex;
    padding: 4px 12px;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-1, 4px);
    border-radius: 58px;
    background: #ee3c3e;

    color: var(--escala-de-cinza-0, #fff);

    font-family: Manrope, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0.14px;
  }

  @media (max-width: 726px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

export const MainCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  padding-top: 4px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(107deg, #64b8fb -28.94%, #8765ee 114.67%);
  }

  @media (min-width: 1024px) {
    flex-direction: row;
    min-height: 500px;
  }
`;

export const LeftColumn = styled.div`
  flex: 1;
  display: flex;
  padding: 26px;
  flex-direction: column;
  align-items: flex-start;
  gap: 26px;
  align-self: stretch;
`;

export const RightColumn = styled.div`
  width: 100%;
  background-color: white;
  display: flex;
  flex-direction: column;
  border: 0px;

  @media (min-width: 1024px) {
    width: 380px;
    min-width: 380px;
    border-left: 1px solid var(--escala-de-cinza-20, #d6d6d6);
  }
`;

export const LessonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  width: 100%;

  h1 {
    color: #000;

    font-family: Manrope, sans-serif;
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 31px;
  }

  .lesson-badge {
    display: flex;
    text-wrap: nowrap;
    min-width: 100px;
    padding: 5px 10px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 40px;
    background: var(--Secondary-Lils-claro, #f0f2ff);

    color: var(--Primary-Purple, #5768ff);

    font-family: Manrope, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
  }
`;

export const VideoContainer = styled.div`
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16/9;
  margin: 0 auto;
  background-color: #000;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  .video-wrapper {
    width: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.8;
  }

  .play-overlay {
    position: absolute;
    width: 64px;
    height: 64px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.1);
    }

    svg {
      font-size: 32px;
      color: white;
    }
  }
`;

export const ModuleListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

interface ModuleHeaderProps {
  isOpen?: boolean;
  isLast?: boolean;
  /** Profundidade na árvore de módulos (0 = raiz). Controla indentação e tamanho. */
  depth?: number;
}

/** Indentação por nível, com teto para não consumir o espaço do título em árvores profundas. */
const depthIndent = (depth?: number) => `${24 + Math.min(depth || 0, 3) * 20}px`;

const moduleHeaderBackground = (isOpen?: boolean, depth?: number) => {
  const isChild = (depth || 0) > 0;
  if (isOpen) return isChild ? '#7584FF' : '#5768FF';
  return isChild ? '#F7FAFF' : '#EFF6FF';
};

export const ModuleHeader = styled.div<ModuleHeaderProps>`
  min-height: ${(props) => ((props.depth || 0) > 0 ? '64px' : '90px')};
  gap: 40px;
  align-self: stretch;
  padding: 20px 24px;
  padding-left: ${(props) => depthIndent(props.depth)};
  background-color: ${(props) => moduleHeaderBackground(props.isOpen, props.depth)};
  color: ${(props) => (props.isOpen ? 'white' : '#5768FF')};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: ${(props) => (props.isLast ? 'none' : '1px solid #D6D6D6')};
  transition: all 0.2s;

  font-family: Manrope, sans-serif;
  font-size: ${(props) => ((props.depth || 0) > 0 ? '15px' : '16px')};
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.32px;

  .module-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Conector em "L" para evidenciar que o módulo é filho de outro. */
  ${(props) =>
    (props.depth || 0) > 0 &&
    `
    .module-title::before {
      content: '';
      flex-shrink: 0;
      width: 10px;
      height: 10px;
      margin-top: -8px;
      border-left: 2px solid currentColor;
      border-bottom: 2px solid currentColor;
      border-bottom-left-radius: 4px;
      opacity: 0.6;
    }
  `}

  &:hover {
    filter: brightness(0.95);
  }
`;

export const ModuleHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

interface ModuleProgressProps {
  isOpen?: boolean;
}

export const ModuleProgress = styled.div<ModuleProgressProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  .module-progress-text {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0.24px;
    white-space: nowrap;
  }

  .module-progress-bar {
    position: relative;
    width: 96px;
    height: 4px;
    border-radius: 2px;
    overflow: hidden;
    background-color: ${(props) => (props.isOpen ? 'rgba(255, 255, 255, 0.35)' : '#D6E4FF')};

    .module-progress-fill {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      border-radius: 2px;
      background-color: ${(props) => (props.isOpen ? '#FFFFFF' : '#5768FF')};
      transition: width 0.3s;
    }
  }

  @media (max-width: 455px) {
    .module-progress-text {
      font-size: 11px;
      letter-spacing: 0;
    }

    .module-progress-bar {
      width: 64px;
    }
  }
`;

interface ClassItemProps {
  isActive?: boolean;
  locked?: boolean;
  /** Profundidade do módulo dono da aula; indenta junto com o módulo. */
  depth?: number;
}

export const ClassItem = styled.div<ClassItemProps>`
  padding: 16px 24px;
  padding-left: ${(props) => depthIndent(props.depth)};
  border-bottom: 1px solid #d6d6d6;
  background-color: white;
  cursor: ${(props) => (props.locked ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.locked ? 0.6 : 1)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) => !props.locked && 'rgb(247, 251, 255)'};
  }

  .class-title {
    width: 100%;
    color: var(--ARTConcursos-Black, #000);
    font-family: Manrope, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0.32px;

    .date {
      font-weight: 400;
      font-size: 10px;
      line-height: 20px;
      letter-spacing: 0.28px;
      background: var(--escala-de-cinza-20, #d6d6d6);
      padding: 2px 9px;
      border-radius: 10px;
      opacity: 0.6;
    }
  }

  ${(props) =>
    props.isActive &&
    `
    .class-title {
      font-weight: 800;
    }

    background-color: rgb(247, 251, 255);
  `}

  .status-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    > img {
      width: 24px;
      height: 24px;
    }
  }
`;

export const EmptyModuleMessage = styled.div<{ depth?: number }>`
  padding: 16px 24px;
  padding-left: ${(props) => depthIndent(props.depth)};
  border-bottom: 1px solid #d6d6d6;
  background-color: white;
  color: var(--escala-de-cinza-50, #858585);
  font-family: Manrope, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

export const ContentSection = styled.div`
  display: flex;
  width: 100%;
  padding: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 26px;
  align-self: stretch;
  background-color: white;
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const TabsHeader = styled.div`
  display: flex;
  width: 100%;

  overflow-x: auto;
`;

interface TabItemProps {
  isActive?: boolean;
  disabled?: boolean;
  bgIconImage: string;
  bgIconNoActive: string;
}

export const TabItem = styled.div<TabItemProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  align-items: center;
  gap: 8px;
  display: flex;
  padding: 2px 30px 14px 30px;
  flex: 1 0 0;
  border-bottom: ${(props) => (props.isActive ? '1px solid #5B68DF' : '1px solid #e5e7eb')};

  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  position: relative;
  color: ${(props) => (props.isActive ? '#101010' : '#A4A4A4')};
  font-weight: 500;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: all 0.2s;

  &:hover {
    color: ${(props) => !props.disabled && '#111827'};
    cursor: pointer;

    .icon {
      background-image: ${(props) => `url(${props.bgIconImage})`};
    }
  }

  .icon {
    background-image: ${(props) =>
      `url(${props.isActive ? props.bgIconImage : props.bgIconNoActive})`};
    background-size: content;
    background-repeat: no-repeat;
    background-position: center;
    width: 20px;
    height: 20px;
  }

  span {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 25px;
    letter-spacing: -0.18px;
  }
`;

export const TabContent = styled.div`
  width: 100%;
`;

export const DescriptionText = styled.p`
  color: var(--escala-de-cinza-70, #434343);

  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.16px;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  color: #5b68df;
  font-size: 16px;
  font-weight: 500;
`;

export const ButtonSectionsArea = styled.button`
  min-width: 340px;
  min-height: 46px;
  display: flex;
  padding: 14px 10px 14px 16px;
  align-items: center;
  gap: 6px;
  flex: 1 0 0;

  border-radius: 10px;
  border: 1px solid var(--escala-de-cinza-20, #d6d6d6);
  background: var(--escala-de-cinza-0, #fff);
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.08);

  cursor: pointer;

  transition: all 0.2s;

  &:hover {
    filter: brightness(0.95);
  }

  span {
    width: 100%;
    text-align: left;
    color: var(--escala-de-cinza-90, #101010);

    font-family: Manrope, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 0.24px;

    margin-bottom: 2px;
  }

  > span.extras {
    color: var(--escala-de-cinza-50, #858585);
    width: fit-content;
    text-align: right;
    margin-bottom: 2px;
    margin-right: 6px;
    margin-left: auto;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    pointer-events: none;
  }

  @media (max-width: 455px) {
    min-width: 100%;
  }
`;
